import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import PageLayout from '@/components/shared/PageLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Save,
  Download,
  RotateCcw,
  Activity,
  PersonStanding,
  UserPlus,
  LayoutDashboard,
  ChevronDown,
  History,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
import { useClient } from "@/context/client-context";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import TestInputGrid from "@/components/sop-intake/TestInputGrid";
import { PASSIVE_TEST_DATA as DEFAULT_TEST_DATA } from "@/components/sop-intake/constants";
import { TestData } from "@/components/sop-intake/types";
import { useSopIntake } from "@/hooks/use-sop-intake";
import VisioMeter2 from "./Visual-GON/VisioMeter2";
import { SettingsProvider as VGonSettingsProvider } from "./Visual-GON/SettingsContext";
import AnalysisFooterDisplay from "@/components/shared/analysis-footer/AnalysisFooterDisplay";
import { getClientAssessments } from "@/components/sop-intake/services/assessments-api";
import {
  getClientPostureAnalyses,
  getPostureAnalysis,
} from "@/components/posture-analysis/services/posture-analyses-api";
import { ContextualToolbar, ToolbarButtonGroup } from "@/components/ui/contextual-toolbar";
import { ToolbarButton } from "@/components/shared/ToolbarButton";
import type { Assessment } from "@/lib/types";
import { SelectClientDialog } from "@/components/select-client-dialog";
import { formatDateTimeLocale, pickLatestRecordBySortDate, sortRecordsBySortDateDesc } from "@/lib/utils";
import { classifyPosture } from "@/components/posture-analysis/lib/analysis";
import { inferSagittalInputFromCaptured } from '@/lib/posture-analysis/posture-pic-evaluation/strategies/posture-strategy-context';
import { useSettings } from '@/components/settings/lib/settings-context';
import { resolvePostureOrientationStrategyId } from '@/lib/posture-analysis/posture-pic-evaluation/strategies/posture-orientation-strategy-id';
import { ClientPostureOverview } from "@/components/posture-analysis/ClientPostureOverview";
import type { PostureAnalysis } from "@/lib/types";
import logger from '@/lib/logging';
import { computeSopPosture } from '@/lib/posture-analysis/sop-evaluation';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

export default function SopAssessmentPage() {
  const { client, setClient, isClientLoading } = useClient();
  const navigate = useNavigate();
  const location = useLocation();
  const hasAutoRedirectedToLatestRef = useRef(false);
  const [isVGonOpen, setIsVGonOpen] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [selectedTest, setSelectedTest] = useState<{ testKey: string; side: 'L' | 'R' } | null>(null);
  const [priorAssessments, setPriorAssessments] = useState<{ id: string; label: string; createdAt?: Date | null }[]>([]);
  const [showLiveClassification, setShowLiveClassification] = useState(true);
  const [isClientDialogOpen, setIsClientDialogOpen] = useState(false);
  const [latestPostureAnalysis, setLatestPostureAnalysis] = useState<PostureAnalysis | null>(null);
  const [postureClassification, setPostureClassification] = useState<any>(null);
  const [postureSidebarCollapsed, setPostureSidebarCollapsed] = useState(false);
  const { settings } = useSettings();

  useEffect(() => {
    hasAutoRedirectedToLatestRef.current = false;
  }, [client?.id]);

  useEffect(() => {
    const loadAssessments = async () => {
      if (!client?.id) return;
      const list = await getClientAssessments(client.id);
      const formatted = sortRecordsBySortDateDesc(list).map(a => {
        const raw: any = (a as any).date ?? (a as any).createdAt;
        const d = raw && typeof raw.toDate === 'function' ? raw.toDate() : raw instanceof Date ? raw : new Date(raw as any);
        return {
          id: a.id,
          label: formatDateTimeLocale(d),
          createdAt: d
        };
      });
      setPriorAssessments(formatted);
    };
    const fetchLatestPosture = async () => {
      if (!client?.id) return;
      const analyses = await getClientPostureAnalyses(client.id);
      const latest = pickLatestRecordBySortDate(analyses);
      if (latest) {
        const analysisData = await getPostureAnalysis(client.id, latest.id);
        if (analysisData) {
          setLatestPostureAnalysis(analysisData as PostureAnalysis);
        }
      }
    };
    loadAssessments();
    fetchLatestPosture();
  }, [client?.id]);

  useEffect(() => {
    if (
      !client?.id ||
      hasAutoRedirectedToLatestRef.current ||
      location.pathname.endsWith('/assessment/new')
    ) {
      return;
    }

    let cancelled = false;

    const redirectToLatestAssessment = async () => {
      const assessments = await getClientAssessments(client.id);
      const latest = pickLatestRecordBySortDate(assessments);
      if (cancelled || !latest) return;

      hasAutoRedirectedToLatestRef.current = true;
      navigate(`/clients/${client.id}/assessment/${encodeURIComponent(latest.id)}`, { replace: true });
    };

    void redirectToLatestAssessment();

    return () => {
      cancelled = true;
    };
  }, [client?.id, location.pathname, navigate]);

  const handleOpenVisioMeter = (testKey: string, side: 'L' | 'R') => {
    setSelectedTest({ testKey, side });
    setIsVGonOpen(true);
  };

  const handleVGonConfirm = (testKey: string, side: 'L' | 'R', value: number, imageData?: string) => {
    const id = `${side}_${testKey}`;
    handleInputChange(id, String(value));

    // Store image data in assessment if provided
    if (imageData) {
      const imageKey = `${id}_image`;
      handleInputChange(imageKey, imageData);
    }
  };

  const {
    formValues,
    setFormValues,
    handleInputChange,
    analysis,
    clientName,
    setClientName,
    handleSave,
    handleExportPdf,
    resetForm,
    lastAnalysis,
    saveStatus,
    // New lateralization data for LiveAnalysisFooter
    lateralizationCounts,
    completedTestsCount,
    totalTestsCount,
    overallPattern,
    phaseGoals
  } = useSopIntake(client, setClient);

  useEffect(() => {
    if (client) {
      setClientName(client.name);
      setClient(client);
    }
  }, [client, setClientName, setClient]);

  // Live classification for posture
  useEffect(() => {
    if (!latestPostureAnalysis?.captured || !latestPostureAnalysis?.analysis) {
        setPostureClassification(null);
        return;
    }

    const { sagittal } = inferSagittalInputFromCaptured(latestPostureAnalysis.captured);
    const strategyId = resolvePostureOrientationStrategyId(
      settings.postureOrientationStrategyId,
      settings.experimentalFeaturesEnabled === true,
      true
    );
    
    // We get completedTestsCount and totalTestsCount from useSopIntake
    const sopCompletionRatio = totalTestsCount > 0 ? Math.min(1, completedTestsCount / totalTestsCount) : 0;
    const sopTablePosture = computeSopPosture(formValues as any, analysis ?? null);

    const classifyOptions = {
        strategyId,
        capturedForStrategy: latestPostureAnalysis.captured,
        strategyInput: {
            ...(sagittal ? { sagittal } : {}),
            sopCompletionRatio,
            sopTablePosture: { pattern: sopTablePosture.pattern },
        }
    };

    classifyPosture(latestPostureAnalysis.analysis as any, 'Unknown', classifyOptions)
        .then(setPostureClassification)
        .catch(err => {
            logger.error('Failed to calculate live SOP posture classification:', err);
            setPostureClassification(null);
        });

  }, [latestPostureAnalysis, completedTestsCount, totalTestsCount, settings.postureOrientationStrategyId, formValues, analysis]);

  if (isClientLoading) {
    return (
      <PageLayout
        title="Loading..."
        isLoading={true}
      >
        <div className="flex items-center justify-center py-8">
          <p className="text-muted-foreground">Loading client data...</p>
        </div>
      </PageLayout>
    );
  }

  if (!client) {
    return (
      <PageLayout
        title="SOP Assessment"
        subtitle="Record assessment values to analyze movement patterns and identify postural compensations"
        compactContent
      >
        <div className="flex flex-col gap-8 py-4">
          <div className="flex items-start">
            <Card className="max-w-lg w-full">
              <CardHeader>
                <div className="bg-primary/10 text-primary p-3 rounded-full mb-4 w-fit">
                  <PersonStanding className="h-8 w-8" />
                </div>
                <CardTitle>Select a Client to Begin</CardTitle>
                <CardDescription>
                  To start a new SOP assessment, select an existing client or create a new one.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col sm:flex-row gap-3">
                  <SelectClientDialog open={isClientDialogOpen} onOpenChange={setIsClientDialogOpen}>
                    <Button className="h-12" onClick={() => setIsClientDialogOpen(true)}>
                      <UserPlus className="mr-2 h-4 w-4" />
                      Select Existing Client
                    </Button>
                  </SelectClientDialog>
                  <Button asChild variant="secondary" className="h-12">
                    <Link to="/clients/new">
                      Create New Client
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </PageLayout>
    );
  }

  // Use default test data for now (will be replaced with narrative rule system)
  const testDataMap: TestData = DEFAULT_TEST_DATA;
  const renderPostureView = () => {
    return (
        <ClientPostureOverview
            analysis={latestPostureAnalysis}
            classification={postureClassification}
            layout="sidebar"
            showIntegratedSopSummary={!!postureClassification || !!analysis}
            sopBucketAnalysis={analysis}
        />
    );
  };

  return (
    <PageLayout
      title="SOP Assessment"
      subtitle="Record assessment values to analyze movement patterns and identify postural compensations"
      compactContent
    >
      <div className="flex flex-col gap-4 pt-2">
        <ContextualToolbar position="sticky-top">
          <div className="flex w-full flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <ToolbarButtonGroup align="left" className="sm:w-auto">
              <DropdownMenu className="font-bold">
                <DropdownMenuTrigger className="inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-bold ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-transparent hover:bg-muted/50">
                  <LayoutDashboard className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">
                    {/* Since we don't have a selected ID state easily accessible here without parsing URL, 
                        we can default to 'New Assessment' if we are on the main page, or show the date if we can find it.
                        However, SopAssessmentPage seems to be the main entry. 
                        Let's just show "New Assessment" or "Select Assessment" logic.
                        Actually, looking at the previous select, it was just a list of previous assessments.
                        Let's mirror the Posture Analysis one: "New Assessment" if creating new, or the date if viewing one.
                        But here we are in SopAssessmentPage which seems to handle both? 
                        Wait, the previous select had `defaultValue=""`. 
                        Let's stick to the "New Assessment" label for the trigger if nothing selected, 
                        but since this page might be used for viewing too (based on URL params handled by router?),
                        we might need to know if we are viewing one.
                        The `priorAssessments` logic suggests we are just listing them.
                        Let's use a generic label "Assessments" or "New Assessment" like in the screenshot.
                    */}
                    New Assessment
                  </span>
                  <ChevronDown className="ml-2 h-4 w-4 opacity-50 hidden sm:block" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  <DropdownMenuItem onClick={() => {
                    resetForm();
                    navigate(`/clients/${client.id}/sop-intake`);
                  }}>
                    <LayoutDashboard className="mr-2 h-4 w-4" />
                    New Assessment
                  </DropdownMenuItem>
                  {priorAssessments.length > 0 && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="flex items-center gap-2">
                        <History className="h-4 w-4" />
                        Previous Assessments
                      </DropdownMenuLabel>
                      {priorAssessments.map((a) => (
                        <DropdownMenuItem
                          key={a.id}
                          onClick={() => navigate(`/clients/${client.id}/assessment/${encodeURIComponent(a.id)}`)}
                        >
                          {a.label}
                        </DropdownMenuItem>
                      ))}
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              <ToolbarButton
                onClick={() => setShowLiveClassification(!showLiveClassification)}
                title="Toggle live classification"
              >
                <Activity className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Analysis</span>
              </ToolbarButton>

            </ToolbarButtonGroup>

            <ToolbarButtonGroup align="right" className="w-full sm:w-auto justify-end">
              <AlertDialog open={showReset} onOpenChange={setShowReset}>
                <AlertDialogTrigger asChild>
                  <ToolbarButton aria-label="Reset assessment" title="Reset assessment">
                    <RotateCcw className="h-4 w-4 sm:mr-2" />
                    <span className="hidden sm:inline">Reset</span>
                  </ToolbarButton>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Reset Assessment?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will clear all test results and analysis. This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => { resetForm(); setShowReset(false); }}>
                      Reset
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              <ToolbarButton
                onClick={handleSave}
                disabled={saveStatus === 'saving'}
                isPrimary
                aria-label={saveStatus === 'saving' ? 'Saving...' : 'Save'}
                title="Save"
              >
                <Save className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">
                  {saveStatus === 'saving' ? 'Saving...' : 'Save'}
                </span>
              </ToolbarButton>

              <ToolbarButton
                onClick={handleExportPdf}
                aria-label="Export PDF"
                title="Export PDF"
              >
                <Download className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">PDF</span>
              </ToolbarButton>
            </ToolbarButtonGroup>
          </div>
        </ContextualToolbar>

        {showLiveClassification && (
          <AnalysisFooterDisplay
            variant="sop"
            leftCount={lateralizationCounts.left}
            rightCount={lateralizationCounts.right}
            intactCount={lateralizationCounts.intact}
            totalTests={totalTestsCount}
            completedTests={completedTestsCount}
            formValues={formValues}
            analysis={analysis}
            clientClassificationCode={client?.classification}
            phaseGoals={phaseGoals}
            createdAt={null}
            priorAssessments={priorAssessments}
            onSelectAssessment={(id) => {
              navigate(`/clients/${client.id}/assessment/${encodeURIComponent(id)}`);
            }}
          />
        )}

        <div className="flex flex-col xl:flex-row gap-6 items-start">
          <div className="flex-1 w-full overflow-hidden">
            <Card className="overflow-x-hidden">
              <div className="p-3 sm:p-6 border-b border-border dark:border-border">
                <h4 className="font-semibold text-base sm:text-lg">Movement Tests</h4>
                <p className="text-xs sm:text-sm text-muted-foreground">Enter measurements and test results</p>
              </div>
              <div className="p-2 sm:p-4 md:p-6 pt-4 sm:pt-6">
                <TestInputGrid
                  formValues={formValues}
                  onInputChange={handleInputChange}
                  onOpenVisioMeter={handleOpenVisioMeter}
                  testDataMap={testDataMap}
                />
              </div>
            </Card>
          </div>
          {!postureSidebarCollapsed ? (
            <div className="w-full xl:w-1/3 shrink-0 xl:sticky xl:top-[80px] transition-all duration-300">
              <div className="relative group">
                <button
                  onClick={() => setPostureSidebarCollapsed(true)}
                  className="absolute -left-3 top-4 h-6 w-6 rounded-full border bg-background shadow-sm z-10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-accent"
                  title="Collapse Posture View"
                >
                  <ChevronRight className="h-3 w-3" />
                </button>
                {renderPostureView()}
              </div>
            </div>
          ) : (
            <div className="hidden xl:flex xl:w-10 shrink-0 xl:sticky xl:top-[80px] flex-col items-center pt-4 border-l border-dashed border-border/50 h-[calc(100vh-120px)] transition-all duration-300">
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8 rounded-full border bg-background shadow-sm hover:bg-primary/10 hover:text-primary transition-colors"
                onClick={() => setPostureSidebarCollapsed(false)}
                title="Expand Posture View"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="mt-8 flex flex-col items-center gap-4 py-4 cursor-pointer group" onClick={() => setPostureSidebarCollapsed(false)}>
                <div className="p-2 rounded-full bg-primary/5 text-primary group-hover:bg-primary/10 transition-colors">
                  <PersonStanding className="h-5 w-5" />
                </div>
                <div className="[writing-mode:vertical-lr] text-[10px] font-bold text-muted-foreground/60 tracking-[0.2em] uppercase select-none group-hover:text-primary transition-colors">
                  Posture Twin
                </div>
              </div>
            </div>
          )}
        </div>

        <VGonSettingsProvider>
          <VisioMeter2
            isOpen={isVGonOpen}
            onClose={() => {
              setIsVGonOpen(false);
              setSelectedTest(null);
            }}
            formValues={formValues}
            onValueConfirm={handleVGonConfirm}
            onValueChange={(testKey, side, value) => {
              handleInputChange(`${side}_${testKey}`, String(value));
            }}
            initialTest={selectedTest || undefined}
            testDataMap={testDataMap}
            clientName={client?.name}
          />
        </VGonSettingsProvider>

      </div>
    </PageLayout>
  );
}